import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

type Mode = 'AR' | 'AP';

const BUCKETS = [
  { key: 'current', label: 'Vadesi Gelmemiş' },
  { key: 'days1to30', label: '1-30' },
  { key: 'days31to60', label: '31-60' },
  { key: 'days61to90', label: '61-90' },
  { key: 'over90', label: '90+' },
] as const;

function n(v: number): string {
  return v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * AR / AP aging (yaşlandırma) report. The bucketing endpoints
 * (tradingInvoice.getAgingReport / tradingSupplierInvoice.getApAgingReport) were
 * already built but surfaced on no page — the controller's primary collections /
 * payables tool was invisible. This renders both with an as-of date.
 *
 * Note: balances are summed across currencies without FX normalization (an
 * upstream limitation flagged in the audit); the header states this.
 */
export function TradingAgingReportPage() {
  const { t: tCommon } = useTranslation('common');
  const [mode, setMode] = useState<Mode>('AR');
  const [asOf, setAsOf] = useState<string>(new Date().toISOString().slice(0, 10));
  const asOfDate = new Date(asOf);

  const ar = trpc.tradingInvoice.getAgingReport.useQuery({ asOfDate }, { enabled: mode === 'AR' });
  const ap = trpc.tradingSupplierInvoice.getApAgingReport.useQuery({ asOfDate }, { enabled: mode === 'AP' });

  const active = mode === 'AR' ? ar : ap;
  const summary = active.data?.summary;
  const rows: Array<{ name: string; current: number; days1to30: number; days31to60: number; days61to90: number; over90: number; total: number }> =
    mode === 'AR'
      ? (ar.data?.byCustomer ?? []).map((r) => ({ name: r.customerName, ...r }))
      : (ap.data?.bySupplier ?? []).map((r) => ({ name: r.supplierName, ...r }));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Yaşlandırma Raporu / Aging</h1>
          <p className="text-sm text-muted-foreground">
            {mode === 'AR' ? 'Alıcılar (120) — alacak yaşlandırma' : 'Satıcılar (320) — borç yaşlandırma'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex overflow-hidden rounded-md border">
            {(['AR', 'AP'] as Mode[]).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={cn(
                  'px-3 py-1.5 text-sm',
                  mode === m ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
                )}
              >
                {m === 'AR' ? 'Alacaklar' : 'Borçlar'}
              </button>
            ))}
          </div>
          <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-auto" />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Tutarlar para birimleri ayrıştırılmadan toplanmıştır (FX normalizasyonu yapılmamıştır).
      </p>

      {active.isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
            {BUCKETS.map((b) => (
              <div key={b.key} className="rounded-lg border bg-card p-3">
                <div className="text-xs text-muted-foreground">{b.label}</div>
                <div className="text-base font-semibold tabular-nums">{n(summary?.[b.key] ?? 0)}</div>
              </div>
            ))}
            <div className="rounded-lg border bg-primary/5 p-3">
              <div className="text-xs text-muted-foreground">TOPLAM</div>
              <div className="text-base font-bold tabular-nums">{n(summary?.total ?? 0)}</div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th className="px-3 py-2">{mode === 'AR' ? 'Müşteri' : 'Tedarikçi'}</th>
                  {BUCKETS.map((b) => (
                    <th key={b.key} className="px-3 py-2 text-right">{b.label}</th>
                  ))}
                  <th className="px-3 py-2 text-right">Toplam</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                      {tCommon('noData', { defaultValue: 'No data' })}
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-2">{r.name}</td>
                      {BUCKETS.map((b) => (
                        <td key={b.key} className="px-3 py-2 text-right tabular-nums">{n(r[b.key])}</td>
                      ))}
                      <td className="px-3 py-2 text-right font-medium tabular-nums">{n(r.total)}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {rows.length > 0 && summary && (
                <tfoot>
                  <tr className="border-t-2 font-bold">
                    <td className="px-3 py-2">TOPLAM</td>
                    {BUCKETS.map((b) => (
                      <td key={b.key} className="px-3 py-2 text-right tabular-nums">{n(summary[b.key])}</td>
                    ))}
                    <td className="px-3 py-2 text-right tabular-nums">{n(summary.total)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}
